/**
 * What her browser receives and sends: `api/models.py`'s allowlist, in
 * TypeScript. The server's models are `extra="forbid"`, so a field added here
 * and not there is a 422, and a field the server adds is a decision made in
 * `models.py` first (its CHILD_SOURCES table), never here.
 *
 * Nothing in these types can hold a key: the browser never marks (surfaces.md
 * rule 1). `ChildTruth` arrives only when an item closes.
 */
import { get, post } from "../lib/http";

export interface ChildOption {
  id: string;
  label: string;
}

export interface ChildDrawing {
  id: string;
  params: Record<string, number | string | number[]>;
}

export interface ChildSurface {
  id: string;
  mode: string;
}

export interface ChildItem {
  handle: string;
  surface: ChildSurface;
  lang: string;
  prompt: string;
  /** Drawn in this order, which the server chose for this item and logged; a digit picks by place. */
  options: ChildOption[] | null;
  drawing: ChildDrawing | null;
  skip_allowed: boolean;
  /** The item.shown's attempt; `answer.submitted.attempt` must equal it. */
  attempt: number;
  /** What sits beside the item, from its Topic's subjects: the periodic table on a chemistry item. */
  aids: "periodic_table"[];
}

export interface ChildTruth {
  handle: string;
  text: string;
  tex: string;
  option: string | null;
}

export interface ChildStep {
  say: string;
  child: Record<string, unknown> | null;
  ask: { options: ChildOption[] } | null;
}

export interface ChildBeat {
  focus: Record<string, unknown> | null;
  steps: ChildStep[];
}

export interface ChildExplanation {
  ref: string;
  rev: number;
  lang: string;
  drawing: string | null;
  beats: ChildBeat[];
}

export interface ChildDoor {
  explanation: string;
  rev: number;
}

export interface ChildVerdict {
  answer: string;
  mode: "practice" | "test";
  word: "Taip." | "Ne visai." | "Tikrinama…" | null;
  door: ChildDoor | null;
  closed: boolean;
  truth: ChildTruth | null;
}

export interface ChildReviewItem {
  item: ChildItem;
  answer: Record<string, unknown> | null;
  skipped: boolean;
  truth: ChildTruth | null;
  door: ChildDoor | null;
}

export interface ChildNext {
  kind: "item" | "test_intro" | "review" | "end" | "wait" | "tool";
  mode?: "practice" | "test" | null;
  item?: ChildItem | null;
  test?: string | null;
  review?: ChildReviewItem[] | null;
  reason?: string | null;
}

export interface Timing {
  shown_at: number;
  first_key_ms: number | null;
  submit_ms: number;
  hidden_ms: number;
}

export interface Env {
  layout_hint: "lt" | "lt-std" | "us" | "other" | "unknown";
  layout_basis: string;
  layout_map: Record<string, string> | null;
  numpad: boolean | null;
}

export type TraceEntry = [number, string, string, string, string] | [number, string, string, string, string, string];

export interface AnswerIn {
  id: string;
  item: string;
  attempt: number;
  surface: { rev: number };
  input: Record<string, unknown>;
  trace: TraceEntry[];
  timing: Timing;
  env: Env;
  trace_truncated?: number;
  client_at?: number;
}

export interface SkipIn {
  id: string;
  item: string;
  attempt: number;
  input: Record<string, unknown>;
  timing: { skip_ms: number; hidden_ms: number; first_key_ms?: number | null };
  trace: TraceEntry[];
  client_at?: number;
}

export interface ClientEvent {
  id: string;
  type: string;
  body: Record<string, unknown>;
  client_at?: number;
}

export interface Receipt {
  id: string;
  status: string;
  error?: string;
}

export interface Picker {
  shared: boolean;
  names: string[];
  last: number | null;
}

/** The slice of her graph (surfaces.md §5.3), `GET /api/v1/child/slice`: a
 *  segment is filled only by her own explicit passes (engine `slice_filled`). */
export interface Slice {
  subjects: {
    subject: string;
    title: string;
    topics: { topic: string; title: string; moves: { move: string; filled: boolean }[] }[];
  }[];
}

const C = "/api/v1/child";

export const childApi = {
  enroll: (code: string, label: string) => post<{ enrolled: boolean; shared: boolean }>(`${C}/enroll`, { code, label }),
  picker: () => get<Picker>(`${C}/picker`),
  sitting: (id: string, pick: number | null, build: string) =>
    post<{ session: string; name: string }>(`${C}/sitting`, { id, pick, build, client_at: Date.now() }),
  notMe: (b: { disown_id: string; id: string; pick: number; at_item?: string; build: string }) =>
    post<{ session: string; name: string }>(`${C}/sitting/not-me`, { ...b, client_at: Date.now() }),
  end: (active_ms: number) => post<{ ended: boolean }>(`${C}/sitting/end`, { active_ms }),
  next: () => get<ChildNext>(`${C}/next`),
  answer: (a: AnswerIn) => post<ChildVerdict>(`${C}/answers`, a),
  skip: (s: SkipIn) => post<{ answer: string; duplicate: boolean }>(`${C}/answers/skip`, s),
  openExplanation: (b: {
    id: string;
    item: string;
    explanation: string;
    door: "after_wrong" | "reopen" | "post_test";
    answer?: string;
    test?: string;
  }) => post<ChildExplanation>(`${C}/explanations/open`, { ...b, client_at: Date.now() }),
  events: (events: ClientEvent[]) => post<{ receipts: Receipt[] }>(`${C}/events`, { events }),
  raise: (b: { id: string; where: "item" | "explanation" | "idle"; item?: string; test?: string }) =>
    post<{ request: string; state: string }>(`${C}/requests`, { ...b, client_at: Date.now() }),
  withdraw: (id: string, over: string) =>
    post<{ request: string; state: string }>(`${C}/requests/withdraw`, { id, over, client_at: Date.now() }),
  state: () => get<{ cursor: string; lang: string | null; taken?: boolean }>(`${C}/state`),
  slice: () => get<Slice>(`${C}/slice`),
};
